import * as THREE from 'three';
import { hustleOpen } from '../content/hustles';
import type { Choice, Scene } from '../content/types';
import { angleDelta, clamp } from '../core/math';
import type { NpcAgent } from '../entities/NpcManager';
import { nightOf, usedTonight, useTonight } from '../state/daily';
import type { Interactions } from '../systems/Interactions';
import { buildCar, type BuiltCar } from '../world/builders/vehicles';
import { LOTS, SPOTS, type Vec2 } from '../world/layout';
import { roomSlotAt } from '../world/rooms';
import type { Activities } from './Activities';
import type { Driving } from './Driving';
import type { GameServices } from './services';

export const SHINE_RUN = { perJar: 45, cleanBonus: 20, fine: 75, minJars: 2, maxJars: 6, stops: 3 } as const;
const FAR = { x: 1e6, z: 1e6 };
/** Deputy Boyd's beat: up Main Street, across North Road, down Possum Trot, and back again. */
const BEAT: readonly Vec2[] = [
  { x: 0.4, z: 50 },
  { x: 0.4, z: -48 },
  { x: -63.6, z: -48 },
  { x: -63.6, z: 36 },
];
const LEGS = BEAT.slice(1).map((b, i) => {
  const a = BEAT[i] as Vec2;
  return { a, b, len: Math.hypot(b.x - a.x, b.z - a.z) };
});
const BEAT_LENGTH = LEGS.reduce((sum, l) => sum + l.len, 0);
const PATROL_SPEED = 7;
const BEAM = { length: 15, reach: 11.5, spot: 3.4 };

interface Customer {
  who: string;
  where: string;
  at: Vec2;
  line: string;
}

/** Terry's customers. None of them are named on any paperwork. */
export const CUSTOMERS: readonly Customer[] = [
  {
    who: 'Rodeo Randy',
    where: 'behind the rodeo bleachers',
    at: { x: SPOTS.bleachers.x, z: SPOTS.bleachers.z + 4.5 },
    line: "Randy tucks the jars in his boots. Both boots. 'For the bull. He gets nervous.'",
  },
  {
    who: 'Junkyard Jerry',
    where: 'the junkyard gate',
    at: SPOTS.junkyardGate,
    line: "Jerry weighs a jar in his hand like scrap. 'Good heft.' He pays in cash that smells like WD-40.",
  },
  {
    who: 'Hank',
    where: 'the bunker on Lot 15',
    at: LOTS.hank.stoop,
    line: "Hank takes the jars through a slot in the door. 'Did anyone follow you? Don't answer. They'd hear.'",
  },
  {
    who: 'Possum Pete',
    where: 'his school bus in the Back Forty',
    at: SPOTS.pete,
    line: "Pete holds a jar up to the moon. 'Uncle Rudy's recipe. I'd know it anywhere.' He pays you in quarters and one gold tooth. You give back the tooth.",
  },
  {
    who: 'Lurlene',
    where: 'the side door of the Rusty Spur',
    at: { x: SPOTS.barDoor.x + 7, z: SPOTS.barDoor.z + 0.6 },
    line: "Lurlene hides the jars behind the Fire Bawl. 'Top shelf. Literally. Nobody can reach it.'",
  },
  {
    who: 'Gus',
    where: 'the gun range counter',
    at: SPOTS.gunCounter,
    line: "Gus checks one jar for clarity, sniffs it, and nods. 'Cleaner than my rifle. Here.'",
  },
  {
    who: 'Cooter',
    where: "Cooter's back lot",
    at: SPOTS.cooter,
    line: "Cooter puts the jars in the trunk of a car that's for sale. 'Comes with the car now. Value-add.'",
  },
  {
    who: 'a church deacon',
    where: 'the BMX field, by the old couch',
    at: SPOTS.bmxField,
    line: "A man in a church sweater takes the jars without making eye contact. 'For the... communion.' He pays generously.",
  },
];

/**
 * How a run splits up: up to three drops, one or two jars each, from what you've got
 * (Terry won't send you out with more than six).
 */
export function splitJars(have: number): number[] {
  let jars = Math.min(SHINE_RUN.maxJars, Math.max(0, Math.floor(have)));
  const stops = Math.min(SHINE_RUN.stops, Math.ceil(jars / 2));
  const out: number[] = [];
  for (let i = 0; i < stops; i++) {
    const left = stops - i - 1;
    const n = left === 0 ? Math.min(2, jars) : Math.max(1, Math.min(2, jars - left));
    jars -= n;
    out.push(n);
  }
  return out;
}

interface Stop extends Customer {
  jars: number;
  done: boolean;
}

interface Run {
  stops: Stop[];
  night: number;
  /** Worst it ever got (a clean run never got past half). */
  worst: number;
}

interface Patrol {
  car: BuiltCar;
  beam: THREE.Mesh;
  spot: THREE.Mesh;
  /** Meters along the beat, and which way he's headed. */
  d: number;
  dir: 1 | -1;
  /** Where the spotlight's hitting the ground. */
  aim: THREE.Vector3;
}

/**
 * Shine Runs: Trenchcoat Terry's got customers all over the county and you've got a
 * still. After dark, take your own jars to three drop-offs for twice what Terry pays.
 * Deputy Boyd cruises the park roads sweeping a spotlight; get lit up long enough and
 * he pulls you over, takes the jars and writes you a ticket.
 */
export class ShineRuns {
  private run: Run | null = null;
  private patrol: Patrol | null = null;
  /** 0..1: how sure Boyd is that you're up to something. */
  suspicion = 0;
  private readonly tmp = new THREE.Vector3();
  private readonly tmp2 = new THREE.Vector3();

  constructor(
    private readonly s: GameServices,
    act: Activities,
    private readonly driving: Driving,
  ) {
    act.talkChoices.push((agent) => this.offer(agent));
  }

  get active(): boolean {
    return this.run !== null;
  }

  register(interactions: Interactions): void {
    for (let i = 0; i < SHINE_RUN.stops; i++) {
      interactions.add({
        id: `shine-drop-${i}`,
        at: () => {
          const st = this.run?.stops[i];
          return st && !st.done ? st.at : FAR;
        },
        radius: 2.4,
        priority: 1.5,
        prompt: () => {
          const st = this.run?.stops[i];
          return st && !st.done ? `Drop off ${st.jars} jar${st.jars > 1 ? 's' : ''} for ${st.who}` : null;
        },
        action: () => this.drop(i),
        icon: ':moonshine:',
      });
    }
  }

  /**
   * New game, a load, or back to the title. A run in progress isn't saved, so if the save
   * says one was underway tonight, Terry will take you out again.
   */
  reset(): void {
    const { store } = this.s;
    const active = store.flag('shine_active');
    this.end();
    if (typeof active === 'number' && active === nightOf(store)) store.setFlag('shine_run', `${active}:0`);
  }

  /** The run's over: delivered, busted, or out of night. */
  private end(): void {
    this.run = null;
    this.endPatrol();
    this.suspicion = 0;
    this.s.store.setFlag('shine_active', false);
  }

  private offer(agent: NpcAgent): Choice[] {
    if (agent.def.id !== 'terry') return [];
    const { store } = this.s;
    if (!hustleOpen(store.state, 'shinerun')) return [];
    return [
      {
        label: ':moonshine: Run shine for Terry tonight',
        hint: `$${SHINE_RUN.perJar}/jar · 3 drops`,
        requires: () => {
          if (this.run) return "You're already on a run";
          if (usedTonight(store, 'shine_run') > 0) return 'One run a night. Boyd gets suspicious.';
          if (store.count('shine') < SHINE_RUN.minJars)
            return `Bring at least ${SHINE_RUN.minJars} jars of your own`;
          const h = store.minutes / 60;
          if (h >= 2 && h < 19) return 'Runs go after dark';
          return true;
        },
        run: () => this.start(),
      },
    ];
  }

  private start(): Scene {
    const { store, rng } = this.s;
    useTonight(store, 'shine_run');
    store.setFlag('shine_active', nightOf(store));
    const split = splitJars(store.count('shine'));
    const stops: Stop[] = rng
      .shuffle(CUSTOMERS)
      .slice(0, split.length)
      .map((c, i) => ({ ...c, jars: split[i] ?? 1, done: false }));
    this.run = { stops, night: nightOf(store), worst: 0 };
    this.suspicion = 0;
    this.startPatrol();
    const total = stops.reduce((a, b) => a + b.jars, 0);
    const list = stops
      .map((st) => `• ${st.who}, ${st.where}: ${st.jars} jar${st.jars > 1 ? 's' : ''}`)
      .join('\n');
    return {
      title: 'Trenchcoat Terry',
      speaker: { id: 'terry', name: 'Entrepreneur', voice: 240 },
      text: `Terry hands you a scrap of a Waffle House napkin with three names on it. 'Cash on delivery. ${SHINE_RUN.perJar} a jar. Deputy Boyd's out on the park roads tonight with that spotlight of his. He sees you twice, you're done. Stay in the dark, stay off Main Street, and be done before you pass out at 3.'\n\n${list}\n\n${total} jars, $${total * SHINE_RUN.perJar} if they all get there.`,
      choices: [],
      leave: "Let's ride",
    };
  }

  private drop(i: number): void {
    const run = this.run;
    const st = run?.stops[i];
    if (!run || !st || st.done) return;
    const { store, ui } = this.s;
    if (!store.has('shine', st.jars)) {
      store.toast(
        `You're short on jars for ${st.who}. Somebody's been drinking the merchandise.`,
        'bad',
        ':moonshine:',
      );
      return;
    }
    store.take('shine', st.jars);
    st.done = true;
    const pay = st.jars * SHINE_RUN.perJar;
    store.incr('shineSold', st.jars);
    store.apply({ money: pay, clout: 2 });
    this.s.sfx('cash');
    ui.modal.show({
      title: st.who,
      art: ':moonshine:',
      text: `${st.line}\n\n+$${pay}`,
      choices: [],
      leave: "Pleasure doin' business",
    });
    if (run.stops.every((x) => x.done)) {
      const clean = run.worst < 0.5;
      if (clean) store.apply({ money: SHINE_RUN.cleanBonus });
      store.incr('shineRuns');
      store.unlock('thunder_road');
      this.end();
      window.setTimeout(
        () =>
          store.toast(
            clean
              ? `Clean run! Terry slips you a $${SHINE_RUN.cleanBonus} bonus. 'Boyd never even saw you.'`
              : 'Run done. Terry says you drive like a revenuer is after you. He means it as a compliment.',
            'good',
            ':moonshine:',
          ),
        400,
      );
    }
  }

  // --- Deputy Boyd ---------------------------------------------------------------------------

  private startPatrol(): void {
    this.endPatrol();
    const { world, rng } = this.s;
    const car = buildCar({ kind: 'cop', color: '#1d1f24' });
    car.group.traverse((o) => (o.userData.noBatch = true));
    world.dynamic.add(car.group);
    const beamGeo = new THREE.ConeGeometry(BEAM.spot * 0.9, BEAM.length, 18, 1, true);
    beamGeo.translate(0, -BEAM.length / 2, 0);
    beamGeo.rotateX(-Math.PI / 2);
    const glow = (opacity: number): THREE.MeshBasicMaterial =>
      new THREE.MeshBasicMaterial({
        color: '#fff4cc',
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
    const beam = new THREE.Mesh(beamGeo, glow(0.1));
    const spotMat = glow(0.32);
    // Sweeps over the polygon-offset gravel; keep it on top at low angles.
    spotMat.polygonOffset = true;
    spotMat.polygonOffsetFactor = -4;
    spotMat.polygonOffsetUnits = -4;
    const spot = new THREE.Mesh(new THREE.CircleGeometry(BEAM.spot, 28), spotMat);
    spot.rotation.x = -Math.PI / 2;
    for (const m of [beam, spot]) {
      m.castShadow = m.receiveShadow = false;
      m.userData.noBatch = true;
    }
    world.dynamic.add(beam, spot);
    this.patrol = {
      car,
      beam,
      spot,
      d: rng.range(0, BEAT_LENGTH),
      dir: rng.chance(0.5) ? 1 : -1,
      aim: new THREE.Vector3(),
    };
  }

  private endPatrol(): void {
    const p = this.patrol;
    if (!p) return;
    for (const o of [p.car.group, p.beam, p.spot]) {
      o.removeFromParent();
      o.traverse((m) => {
        if (m instanceof THREE.Mesh) m.geometry.dispose();
      });
    }
    this.patrol = null;
  }

  update(dt: number, time: number): void {
    const run = this.run;
    const { store } = this.s;
    if (!run) return;
    // Daylight (or passing out at 3) ends it: the jars stay yours, the customers go to bed.
    const h = store.minutes / 60;
    if (h >= 3 && h < 19) {
      this.end();
      store.toast(
        "Too late: Terry's customers went to bed. You've still got the jars.",
        'bad',
        ':moonshine:',
      );
      return;
    }
    const p = this.patrol;
    if (!p) return;
    // Drive the beat, turning around at each end.
    p.d += dt * PATROL_SPEED * p.dir;
    if (p.d > BEAT_LENGTH) {
      p.d = BEAT_LENGTH;
      p.dir = -1;
    } else if (p.d < 0) {
      p.d = 0;
      p.dir = 1;
    }
    let along = p.d;
    let leg = LEGS[LEGS.length - 1] as (typeof LEGS)[number];
    for (const l of LEGS) {
      if (along <= l.len) {
        leg = l;
        break;
      }
      along -= l.len;
    }
    const k = clamp(along / leg.len, 0, 1);
    const x = leg.a.x + (leg.b.x - leg.a.x) * k;
    const z = leg.a.z + (leg.b.z - leg.a.z) * k;
    const heading = Math.atan2((leg.b.z - leg.a.z) * p.dir, (leg.b.x - leg.a.x) * p.dir);
    const g = p.car.group;
    g.position.set(x, 0, z);
    // Cars face +x: rotation.y = -atan2(dz, dx) points them down the road.
    g.rotation.y = -heading;
    for (const w of p.car.wheels) w.rotation.y -= dt * 20;
    // The spotlight sweeps across the yards on both sides, a little ahead.
    const sweep = Math.sin(time * 0.9) * 1.35;
    const dirX = Math.cos(heading + sweep);
    const dirZ = Math.sin(heading + sweep);
    p.aim.set(x + dirX * BEAM.reach, 0.04, z + dirZ * BEAM.reach);
    p.spot.position.copy(p.aim);
    p.beam.position.set(x, 1.6, z);
    p.beam.lookAt(p.aim.x, 0, p.aim.z);
    const [red, blue] = p.car.lightBar ?? [];
    const busted = this.suspicion >= 1;
    if (red && blue) {
      const on = busted ? Math.sin(time * 14) > 0 : false;
      red.emissiveIntensity = busted ? (on ? 3 : 0.2) : 0.3;
      blue.emissiveIntensity = busted ? (on ? 0.2 : 3) : 0.3;
    }

    // Are you in the light? (Inside somewhere, you're safe. The Back Forty's past his beat.)
    const you = this.driving.active ? this.driving.position : this.s.player.position;
    const inside = roomSlotAt(you.x) >= 0;
    const lit =
      !inside &&
      (Math.hypot(you.x - p.aim.x, you.z - p.aim.z) < BEAM.spot + 0.4 ||
        (Math.hypot(you.x - x, you.z - z) < 6 &&
          Math.abs(angleDelta(heading, Math.atan2(you.z - z, you.x - x))) < 1.2));
    this.suspicion = clamp(this.suspicion + (lit ? dt * 0.75 : -dt * 0.22), 0, 1);
    run.worst = Math.max(run.worst, this.suspicion);
    if (lit && this.suspicion > 0.3)
      this.s.ui.hud.float(':siren:', this.tmp.set(you.x, 2.6, you.z), '#ff5c5c');
    if (this.suspicion >= 1) this.bust();
  }

  private bust(): void {
    const run = this.run;
    if (!run) return;
    const { store, ui } = this.s;
    const jars = run.stops.filter((st) => !st.done).reduce((a, st) => a + st.jars, 0);
    const lost = Math.min(jars, store.count('shine'));
    store.take('shine', lost);
    const fine = Math.min(SHINE_RUN.fine, Math.floor(store.money));
    if (fine > 0) store.spend(fine);
    store.incr('busted');
    store.addClout(3);
    this.s.sfx('siren');
    this.run = null;
    store.setFlag('shine_active', false);
    ui.modal.show(
      {
        title: 'Deputy Boyd',
        art: ':siren:',
        text: `Red and blue lights. A flashlight in your face. Deputy Boyd leans in, sniffs once and sighs.\n\n'Evenin'. That smell like corn to you? Smells like corn to me.'\n\nHe pours ${lost} jar${lost === 1 ? '' : 's'} out on the gravel, real slow, and writes you a ticket for "transporting agricultural product without a permit."${fine ? ` -$${fine}.` : " You're broke, so he lets the fine slide."}\n\nTerry, somewhere out in the dark, pretends he's never met you.`,
        choices: [],
        leave: 'Yessir, Deputy',
      },
      () => this.endPatrol(),
    );
  }

  objective(): { text: string; target: THREE.Vector3 } | null {
    const run = this.run;
    if (!run) return null;
    const p = this.driving.active ? this.driving.position : this.s.player.position;
    if (roomSlotAt(p.x) >= 0) return null;
    const left = run.stops.filter((st) => !st.done);
    if (!left.length) return null;
    const next = left.reduce((a, b) =>
      Math.hypot(a.at.x - p.x, a.at.z - p.z) <= Math.hypot(b.at.x - p.x, b.at.z - p.z) ? a : b,
    );
    const heat = Math.round(this.suspicion * 100);
    return {
      text: `:moonshine: ${next.who}, ${next.where} · ${left.length} left${heat > 0 ? ` · :siren: ${heat}%` : ''}`,
      target: this.tmp2.set(next.at.x, 0, next.at.z),
    };
  }
}
